import React, { useEffect } from 'react';
import {
    AlertCircle, Trash2, ArrowRight,
} from 'lucide-react';
import { LandingNav } from '../components/LandingNav';
import { LandingFooter } from '../components/LandingFooter';
import step1 from '@/assets/step1.jpeg';
import step2 from '@/assets/step2.jpeg';
import step3 from '@/assets/step3.jpeg';
import step4 from '@/assets/step4.jpeg';
import './landingPage.css';
import './staticPage.css';
import './deleteAccount.css';

interface Step {
    title: string;
    description: string;
    bullets: string[];
    image: string;
    reverse?: boolean;
}

const STEPS: Step[] = [
    {
        title: 'Open the More Menu',
        description:
            'After logging in, you\'ll land on the Home screen. To access account settings, tap on the "More" tab in the bottom navigation bar.',
        bullets: [
            'Open the Raabta app on your device',
            'Log in with your credentials',
            'Locate the bottom navigation bar',
            'Tap on the "More" tab on the right',
        ],
        image: step1,
    },
    {
        title: 'Tap Delete Account',
        description:
            'In the More menu, you\'ll see your profile, children, and various settings. Scroll down to find the "Delete Account" option highlighted in red, just above Sign Out.',
        bullets: [
            'Browse through your profile and settings',
            'Scroll past Notifications, Help & Support, and Privacy & Terms',
            'Locate the "Delete Account" option (red icon)',
            'Tap on "Delete Account" to proceed',
        ],
        image: step2,
        reverse: true,
    },
    {
        title: 'Review What Will Be Deleted',
        description:
            'You\'ll see a warning explaining that this action is permanent. Carefully review the list of data that will be deleted, then choose a reason for leaving.',
        bullets: [
            'Read the "This action cannot be undone" warning',
            'Review the list of data that will be removed',
            'Select a reason for deleting your account',
            'Continue to the final confirmation step',
        ],
        image: step3,
    },
    {
        title: 'Confirm Account Deletion',
        description:
            'On the final screen, type the confirmation phrase and tap the delete button to permanently remove your account. You\'ll receive a confirmation email once the process completes.',
        bullets: [
            'Type the confirmation phrase as instructed',
            'Acknowledge that the action is permanent',
            'Tap the final "Delete My Account" button',
            'Your account will be removed within 30 days',
        ],
        image: step4,
        reverse: true,
    },
];

export const DeleteAccount: React.FC = () => {
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) entry.target.classList.add('is-visible');
                });
            },
            { threshold: 0.15 }
        );
        document.querySelectorAll('.reveal').forEach((el) => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    const scrollToSteps = () => {
        document.getElementById('steps')?.scrollIntoView({ behavior: 'smooth' });
    };

    return (
        <div className="raabta-landing">
            <LandingNav showMenu={false} />

            {/* ── Hero ── */}
            <section className="da-hero">
                <div className="da-hero__blob da-hero__blob--1" />
                <div className="da-hero__blob da-hero__blob--2" />
                <div className="da-hero__blob da-hero__blob--3" />
                <div className="da-hero__blob da-hero__blob--4" />

                <div className="da-hero__inner">
                    <div className="da-hero__pill">
                        <Trash2 size={13} /> Account Management
                    </div>
                    <h1 className="da-hero__title">
                        Delete your <span className="da-hero__title--accent">account?</span>
                    </h1>
                    <p className="da-hero__subtitle">
                        If you've decided to delete your account, please follow the steps below.
                        Be aware that deleting your account is permanent and cannot be undone.
                    </p>
                    <div className="da-hero__cta">
                        <button className="da-btn da-btn--danger" onClick={scrollToSteps}>
                            View Steps <ArrowRight size={16} />
                        </button>
                        <a href="mailto:privacy@raabta.app" className="da-btn da-btn--outline">
                            Need Help?
                        </a>
                    </div>
                    <div className="da-hero__indicators">
                        <span><AlertCircle size={14} /> Permanent Action</span>
                        <span><AlertCircle size={14} /> Data Will Be Lost</span>
                        <span><AlertCircle size={14} /> Cannot Be Undone</span>
                    </div>
                </div>
            </section>

            {/* ── Steps ── */}
            <section id="steps" className="da-steps">
                <div className="raabta-container">
                    {STEPS.map((step, idx) => (
                        <div key={idx} className={`da-step ${step.reverse ? 'da-step--reverse' : ''} reveal`}>
                            <div className="da-step__content">
                                <span className="da-step__num">Step {idx + 1}</span>
                                <h2 className="da-step__title">{step.title}</h2>
                                <p className="da-step__desc">{step.description}</p>
                                <ul className="da-step__bullets">
                                    {step.bullets.map((b, bi) => (
                                        <li key={bi}>
                                            <span className="da-step__bullet-dot" />
                                            {b}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="da-step__visual">
                                <div className="da-screenshot">
                                    <img src={step.image} alt={`Step ${idx + 1}: ${step.title}`} />
                                </div>
                            </div>
                            <div className="da-step__arrow" aria-hidden>
                                <ArrowRight size={16} />
                            </div>
                        </div>
                    ))}
                </div>
            </section>


            <LandingFooter />
        </div>
    );
};
